import React, { useState } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, SubmitHandler } from 'react-hook-form'
import { errorToast, successToast } from 'src/utils/toastService'
import { useAddProductMutation } from 'src/api/manageProductsApi'
import { formatBackendErrorObjToString } from 'src/utils/helpers'

export type IFormInput = {
  title: string
  category: number
  productType: string
  price: number
  unit: string
  description: string
  isActive?: boolean
}

export const formSchema = Yup.object().shape({
  title: Yup.string().required('Product Name is required'),
  productType: Yup.string().required('Product Type is required'),
  category: Yup.number().required('Category is required').min(1, 'Category is required'),
  price: Yup.number().required('Price is required').min(1, 'Price can not be 0'),
  unit: Yup.string().required('Unit is required'),
  description: Yup.string().required('Description is required'),
  isActive: Yup.boolean(),
})

type useAddHookProps = {
  successCallback: () => void
}
const useAddHook = ({ successCallback }: useAddHookProps) => {
  const [file, setFile] = useState<File | null>(null)
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'all',
    defaultValues: {
      title: '',
      productType: '',
      category: 0,
      price: 0,
      unit: '',
      description: '',
      isActive: true,
    },
  })
  const [addProduct, { isLoading: isLoadingAddProduct }] = useAddProductMutation()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setFile(e.target.files[0])
    }
  }

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    console.log(data, file)
    if (!file) {
      errorToast('Product image is required.')
      return
    }

    if (!data.price) {
      errorToast('Amount is required.')
      return
    }

    if (!data.category) {
      errorToast('Category is required.')
      return
    }
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('price', data.price.toString())
    formData.append('unit', data.unit)
    formData.append('description', data.description)
    formData.append('is_active', JSON.stringify(data.isActive))
    formData.append('item_type', data.productType)
    formData.append('supplement_type_id', data.category.toString())
    formData.append('picture', file)
    addProduct(formData)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Product added successfully.')
        reset()
        setFile(null)
        successCallback()
      })
      .catch((err) => {
        console.log(err)
        if (err?.data) {
          const errors = formatBackendErrorObjToString(err.data)
          errors.forEach((error) => {
            errorToast(error)
          })
        } else {
          errorToast('Unable to add product.')
        }
      })
  }

  const resetCategory = () => {
    setValue('category', 0)
  }

  return {
    file,
    handleFileChange,
    onSubmit: handleSubmit(onSubmit),
    control,
    errors,
    isLoadingAddProduct,
    resetCategory,
    watchState: {
      productType: watch('productType'),
    },
    handleChangeStatus: (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue('isActive', !!event.target.checked)
    },
  }
}

export default useAddHook
